/*
 * Shipping calculator in the cart (drawer and /cart) and the free-shipping bar.
 *
 * Prices. Every price comes from the Storefront API: a throwaway copy of the shopper's cart
 * (same lines and discount codes, rendered by snippets/shipping-estimator.liquid) is created
 * with the address in question, in the context of the destination country. That is what checkout
 * does when the shopper types the address: it switches to that country's market and currency
 * (an ES cart sent to the UK is £6 + £10 at checkout, 02.10.2026), so the calculator shows the
 * same delivery methods, amounts and currency. The shopper's own cart never gets an address:
 * checkout opens with nothing the shopper did not type there. (/cart/shipping_rates.json, used
 * until 01.10.2026, saved every address it priced on the cart.)
 *
 * Memory. The country and postcode the shopper priced stay in localStorage for 14 days (one
 * postcode per country) and prices are cached per address and cart, so the price is on screen at
 * once when the drawer is re-opened or redrawn (Dawn after every cart change, the AI chat), on
 * the next page, after a reload and in a new tab. A cart change re-prices the saved address on
 * its own, with a spinner. Switching the site's country starts from that country (with its own
 * remembered postcode); a price in another currency is never reused. Fields typed but not priced
 * yet survive a redraw through sessionStorage.
 *
 * Requests. One request per address and cart, shared by the element and its redrawn copy;
 * quick cart changes are debounced and a superseded request is aborted. 429/5xx/network errors
 * are retried with backoff, every attempt times out, and a final failure says so (with a retry
 * button) instead of spinning forever.
 */
(() => {
  if (customElements.get('shipping-estimator')) return;

  const API = '/api/2026-07/graphql.json';
  const KEY_ADDRESS = 'ctr-shipping-address'; // localStorage
  const KEY_QUOTES = 'ctr-shipping-quotes'; // localStorage
  const KEY_DRAFT = 'ctr-shipping-draft'; // sessionStorage
  const KEY_CLEANUP = 'ctr-shipping-cleanup'; // sessionStorage
  const LEGACY_LOCAL = ['shipping-estimator-address'];
  const LEGACY_SESSION = ['shipping-estimator-draft', 'shipping-estimator-quotes', 'shipping-estimator-cleanup'];

  const ADDRESS_TTL = 14 * 24 * 60 * 60 * 1000;
  const QUOTE_FRESH = 30 * 60 * 1000; // used as is
  const QUOTE_MAX_AGE = 12 * 60 * 60 * 1000; // shown at once and re-checked quietly
  const MAX_QUOTES = 40;
  const DRAFT_TTL = 30 * 60 * 1000;
  const VIEW_TTL = 2 * 60 * 1000;
  const FOCUS_TTL = 20000;
  const DEBOUNCE = 250;
  const BURST_WINDOW = 3000; // another cart within this time = the shopper is still changing it
  const BURST_DEBOUNCE = 1800; // …so only the cart that stays put this long is priced
  const TYPING_DELAY = 600;
  const ATTEMPT_TIMEOUT = 10000;
  const RETRY_DELAYS = [1000, 2500, 5000];
  // Checkout converts the free-delivery threshold at the exchange rate without Shopify's 2%
  // conversion fee, while Shopify.currency.rate includes it. Measured 02.10.2026: PLN 214.50,
  // CHF 45.50, CZK 1198.88, DKK 366.20, SEK 553.12, HUF 18083 for €49 (215 zł free, 214 zł not).
  const CHECKOUT_FX_FEE = 1.02;

  // A postcode or province inside each delivery zone of the countries Shopify cannot price from
  // the country alone (several zones, or a zone defined by provinces). Used for the price before
  // a postcode is typed, and for countries without postcodes. First = main zone. Zones on
  // 02.10.2026: ES mainland / Balearics / Canaries / Ceuta-Melilla, IT mainland / islands,
  // PT mainland / Madeira-Azores; US, RO, IE, AE are one zone made of provinces.
  const ZONES = {
    ES: [{ zip: '28013' }, { zip: '07001' }, { zip: '38002' }, { zip: '51001' }],
    IT: [{ zip: '00185' }, { zip: '90133' }],
    PT: [{ zip: '1100-148' }, { zip: '9000-018' }],
    US: [{ zip: '10001' }],
    RO: [{ zip: '010011' }],
    IE: [{ province: 'D' }],
    AE: [{ province: 'DU' }],
  };
  const NO_POSTCODE = ['AE', 'QA'];
  const OPTIONAL_POSTCODE = ['IE', 'OM'];
  const ZIP_FORMAT = {
    AD: [/^AD\d{3}$/, 'AD500'], AL: [/^\d{4}$/, '1001'], AT: [/^\d{4}$/, '1010'], BA: [/^\d{5}$/, '71000'],
    BE: [/^\d{4}$/, '1000'], BG: [/^\d{4}$/, '1000'], CH: [/^\d{4}$/, '8001'], CY: [/^\d{4}$/, '1010'],
    CZ: [/^\d{3} ?\d{2}$/, '110 00'], DE: [/^\d{5}$/, '10115'], DK: [/^\d{4}$/, '1050'], EE: [/^\d{5}$/, '10111'],
    ES: [/^(0[1-9]|[1-4]\d|5[0-2])\d{3}$/, '28013'], FI: [/^\d{5}$/, '00100'], FR: [/^\d{2} ?\d{3}$/, '75001'],
    GB: [/^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/, 'SW1A 1AA'], GR: [/^\d{3} ?\d{2}$/, '104 31'],
    HR: [/^\d{5}$/, '10000'], HU: [/^\d{4}$/, '1051'], IE: [/^([AC-FHKNPRTV-Y]\d{2}|D6W) ?[\dAC-FHKNPRTV-Y]{4}$/, 'D01 F5P2'],
    IS: [/^\d{3}$/, '101'], IT: [/^\d{5}$/, '00185'], LI: [/^94(8[5-9]|9[0-8])$/, '9490'],
    LT: [/^(LT-?)?\d{5}$/, '01100'], LU: [/^(L-?)?\d{4}$/, '1009'], LV: [/^(LV-?)?\d{4}$/, '1050'],
    MC: [/^980\d{2}$/, '98000'], ME: [/^8\d{4}$/, '81000'], MK: [/^\d{4}$/, '1000'], MT: [/^[A-Z]{3} ?\d{2,4}$/, 'VLT 1010'],
    NL: [/^\d{4} ?[A-Z]{2}$/, '1011 AB'], NO: [/^\d{4}$/, '0010'], OM: [/^\d{3}$/, '100'], PL: [/^\d{2}-?\d{3}$/, '00-001'],
    PT: [/^\d{4}-?\d{3}$/, '1100-148'], RO: [/^\d{6}$/, '010011'], RS: [/^\d{5,6}$/, '11000'], SE: [/^\d{3} ?\d{2}$/, '111 29'],
    SI: [/^(SI-?)?\d{4}$/, '1000'], SK: [/^\d{3} ?\d{2}$/, '811 01'], SM: [/^4789\d$/, '47890'],
    US: [/^\d{5}(-?\d{4})?$/, '10001'], VA: [/^00120$/, '00120'], XK: [/^[1-7]\d{4}$/, '10000'],
  };
  // Every other country, listed as "Other countries" so a shopper from a country we do not ship
  // to can pick it and be told so (checkout does not list them at all).
  const WORLD = ('AF AG AI AM AO AR AS AU AW AX AZ BB BD BF BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CC CD CF CG ' +
    'CI CK CL CM CN CO CR CU CV CW CX DJ DM DO DZ EC EG EH ER ET FJ FK FM FO GA GD GE GF GG GH GI GL GM GN GP ' +
    'GQ GS GT GU GW GY HK HN HT ID IL IM IN IO IQ IR JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LK ' +
    'LR LS LY MA MD MF MG MH ML MM MN MO MP MQ MR MS MU MV MW MX MY MZ NA NC NE NF NG NI NP NR NU NZ PA PE PF ' +
    'PG PH PK PM PN PR PS PW PY RE RU RW SA SB SC SD SG SH SJ SL SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ ' +
    'TK TL TM TN TO TR TT TV TW TZ UA UG UY UZ VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');
  const LANGUAGES = ['AR', 'CS', 'DA', 'DE', 'EL', 'EN', 'ES', 'FI', 'FR', 'HU', 'IT', 'NL', 'NO', 'PL', 'PT_BR',
    'PT_PT', 'RO', 'RU', 'SK', 'SV', 'TR', 'UK', 'HR', 'SL', 'SQ', 'BS', 'AZ', 'NB', 'BG', 'ET', 'LT', 'LV', 'SR'];

  // ── small helpers ────────────────────────────────────────────────────────────────────────

  const storage = (type) => {
    try {
      const store = window[type];
      const probe = '__ctr_probe__';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return store;
    } catch (error) {
      return null; // private mode / blocked: everything still works, just not remembered
    }
  };
  const local = storage('localStorage');
  const session = storage('sessionStorage');
  const read = (store, key) => {
    if (!store) return null;
    try {
      return JSON.parse(store.getItem(key));
    } catch (error) {
      return null;
    }
  };
  const write = (store, key, value) => {
    if (!store) return;
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (error) {
      // full: not remembered
    }
  };
  const sleep = (ms, signal) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      if (signal) {
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
          },
          { once: true }
        );
      }
    });
  const hash = (text) => {
    let value = 5381;
    for (let index = 0; index < text.length; index += 1) value = ((value << 5) + value + text.charCodeAt(index)) | 0;
    return (value >>> 0).toString(36);
  };
  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  // Liquid's `t` filter HTML-escapes translations ("You&#39;ve"); the scripts write them as text.
  const decodeEntities = (value) => {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    const area = document.createElement('textarea');
    area.innerHTML = value;
    return area.value;
  };
  const decodeAll = (object) =>
    Object.keys(object || {}).reduce((out, key) => Object.assign(out, { [key]: decodeEntities(object[key]) }), {});
  const fill = (text, values) =>
    Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(values[key]), text || '');

  const cleanZip = (zip) => (zip || '').toUpperCase().replace(/\s+/g, ' ').trim();
  const compactZip = (zip) => cleanZip(zip).replace(/[\s-]/g, '');
  // What Shopify gets: "00001" → "00-001" (PL), "1100148" → "1100-148" (PT), the rest as typed.
  const apiZip = (country, zip) => {
    const value = cleanZip(zip);
    const digits = value.replace(/[\s-]/g, '');
    if (country === 'PL' && /^\d{5}$/.test(digits)) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (country === 'PT' && /^\d{7}$/.test(digits)) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return value;
  };
  const zipCheck = (country, zip) => {
    const format = ZIP_FORMAT[country];
    if (!zip) return 'empty';
    if (!format) return 'unknown';
    return format[0].test(cleanZip(zip)) ? 'valid' : 'invalid';
  };
  const zipExample = (country) => (ZIP_FORMAT[country] ? ZIP_FORMAT[country][1] : '');

  // ── memory ───────────────────────────────────────────────────────────────────────────────

  // The postcode the old calculator saved (it also sits on old carts, see cleanUpOldCartAddresses).
  const legacyTyped = read(local, 'shipping-estimator-address');
  const legacyCleaned = (() => {
    try {
      if (local) LEGACY_LOCAL.forEach((key) => local.removeItem(key));
      if (session) LEGACY_SESSION.forEach((key) => session.removeItem(key));
    } catch (error) {
      // ignore
    }
    return true;
  })();

  const readAddress = () => {
    const saved = read(local, KEY_ADDRESS);
    if (!saved || !saved.country || Date.now() - (saved.at || 0) > ADDRESS_TTL) return null;
    return saved;
  };
  const saveAddress = (market, country, zip) => {
    const saved = readAddress() || { zips: {} };
    const zips = Object.assign({}, saved.zips || {});
    if (zip) zips[country] = zip;
    else delete zips[country];
    write(local, KEY_ADDRESS, { market, country, zip: zip || '', zips, at: Date.now() });
  };

  const readQuote = (key) => {
    const quotes = read(local, KEY_QUOTES) || {};
    const entry = quotes[key];
    if (!entry || Date.now() - entry.at > QUOTE_MAX_AGE) return null;
    return { quote: entry.quote, fresh: Date.now() - entry.at < QUOTE_FRESH };
  };
  const writeQuote = (key, quote) => {
    const quotes = read(local, KEY_QUOTES) || {};
    quotes[key] = { quote, at: Date.now() };
    Object.keys(quotes)
      .sort((a, b) => quotes[b].at - quotes[a].at)
      .slice(MAX_QUOTES)
      .forEach((old) => delete quotes[old]);
    write(local, KEY_QUOTES, quotes);
  };

  // ── Storefront API ───────────────────────────────────────────────────────────────────────

  // credentials: 'omit' — these requests must not touch the shopper's session or cart cookie.
  // A throwaway cart does not fire add-to-cart analytics (checked 02.10.2026).
  async function post(query, variables, signal) {
    for (let attempt = 0; ; attempt += 1) {
      const attemptController = new AbortController();
      const timer = setTimeout(() => attemptController.abort(), ATTEMPT_TIMEOUT);
      const forward = () => attemptController.abort();
      signal.addEventListener('abort', forward, { once: true });
      let wait = 0;
      try {
        const response = await fetch(API, {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query, variables }),
          signal: attemptController.signal,
        });
        if (response.ok) {
          const body = await response.json();
          if (body && body.data) return body;
          const throttled = body && body.errors && body.errors.some((error) => error.extensions && error.extensions.code === 'THROTTLED');
          if (!throttled) throw Object.assign(new Error('Storefront API error'), { final: true });
        } else if (response.status === 429 || response.status >= 500) {
          wait = (parseFloat(response.headers.get('Retry-After')) || 0) * 1000;
        } else {
          throw Object.assign(new Error(`Storefront API ${response.status}`), { final: true });
        }
      } catch (error) {
        if (signal.aborted || error.final) throw error;
        // network error or the attempt timed out: retried below
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', forward);
      }
      if (attempt >= RETRY_DELAYS.length) throw new Error('Storefront API unavailable');
      await sleep(Math.min(Math.max(wait, RETRY_DELAYS[attempt]), 10000), signal);
    }
  }

  // The delivery options of one throwaway cart: [{ title, amount }] cheapest first, plus the
  // cart subtotal; null when nothing delivers there.
  function readCart(result) {
    const cart = result && result.cart;
    const groups = (cart && cart.deliveryGroups && cart.deliveryGroups.nodes) || [];
    if (!groups.length) return null;
    let currency = null;
    const perGroup = [];
    for (const group of groups) {
      const options = (group.deliveryOptions || [])
        .filter((option) => ['SHIPPING', 'LOCAL', 'PICKUP_POINT'].includes(option.deliveryMethodType))
        .map((option) => ({
          title: option.title || '',
          amount: parseFloat(option.estimatedCost && option.estimatedCost.amount),
          currency: option.estimatedCost && option.estimatedCost.currencyCode,
        }))
        .filter((option) => !Number.isNaN(option.amount));
      if (!options.length) return null;
      currency = currency || options[0].currency;
      options.sort((a, b) => a.amount - b.amount || a.title.localeCompare(b.title));
      perGroup.push(options);
    }
    const cheapest = Math.round(perGroup.reduce((sum, options) => sum + options[0].amount, 0) * 100) / 100;
    const subtotal = cart.cost && cart.cost.subtotalAmount ? parseFloat(cart.cost.subtotalAmount.amount) : null;
    // One shipment (always the case here): its methods as checkout lists them. Several
    // shipments: only the total of the cheapest ones is meaningful.
    const options = perGroup.length === 1 ? perGroup[0].map(({ title, amount }) => ({ title, amount })) : [];
    return { currency, cheapest, options, subtotal };
  }

  // spec: { country, zip, cart, language }. Resolves to a quote:
  //   { status: 'ok', currency, price, several, zones, mainlandFree, options, subtotal }
  //   { status: 'invalid' }  — no delivery to this postcode (Shopify could not place it)
  //   { status: 'none' }     — nothing in this cart can be delivered to this country
  async function fetchQuote({ country, zip, cart, language }, signal) {
    const targets = zip ? [{ zip: apiZip(country, zip) }] : ZONES[country] || [{}];
    const declarations = ['$country: CountryCode!'];
    const variables = { country };
    if (language) {
      declarations.push('$language: LanguageCode!');
      variables.language = language;
    }
    const selections = targets.map((target, index) => {
      const deliveryAddress = { countryCode: country };
      if (target.zip) deliveryAddress.zip = target.zip;
      if (target.province) deliveryAddress.provinceCode = target.province;
      declarations.push(`$cart${index}: CartInput!`);
      variables[`cart${index}`] = {
        lines: cart.lines,
        discountCodes: cart.discountCodes,
        buyerIdentity: { countryCode: country },
        delivery: { addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress } }] },
      };
      return `cart${index}: cartCreate(input: $cart${index}) { cart { cost { subtotalAmount { amount currencyCode } } deliveryGroups(first: 10) { nodes { deliveryOptions { title deliveryMethodType estimatedCost { amount currencyCode } } } } } warnings { code } }`;
    });
    const context = language ? '@inContext(country: $country, language: $language)' : '@inContext(country: $country)';
    const body = await post(`mutation ShippingEstimate(${declarations.join(', ')}) ${context} { ${selections.join(' ')} }`, variables, signal);

    const zones = targets.map((target, index) => readCart(body.data[`cart${index}`]));
    const main = zones[0] || zones.find(Boolean);
    if (!main) {
      const warnings = targets.flatMap((target, index) => ((body.data[`cart${index}`] || {}).warnings || []).map((warning) => warning.code));
      const unavailable = warnings.some((code) => /MERCHANDISE|NOT_ENOUGH_STOCK|OUT_OF_STOCK|UNAVAILABLE/.test(code));
      return { status: zip && !unavailable ? 'invalid' : 'none' };
    }
    const others = zones.filter((zone) => zone && zone !== main);
    const price = Math.min(main.cheapest, ...others.map((zone) => zone.cheapest));
    const zonesDiffer = others.some((zone) => zone.cheapest !== main.cheapest);
    return {
      status: 'ok',
      currency: main.currency,
      price,
      several: main.options.length > 1 || zonesDiffer,
      zones: !zip && zonesDiffer,
      mainlandFree: !zip && main.cheapest === 0 && zonesDiffer,
      options: main.options,
      subtotal: main.subtotal,
    };
  }

  // One request per key, shared by every element that wants it (the drawer and its redrawn
  // copy). Aborted once nobody waits for it any more (the cart changed again meanwhile).
  const inflight = new Map();
  function sharedQuote(key, spec) {
    let entry = inflight.get(key);
    if (!entry) {
      const controller = new AbortController();
      entry = { controller, users: 0 };
      entry.promise = fetchQuote(spec, controller.signal)
        .then((quote) => {
          writeQuote(key, quote);
          return quote;
        })
        .finally(() => {
          if (inflight.get(key) === entry) inflight.delete(key);
        });
      entry.promise.catch(() => {});
      inflight.set(key, entry);
    }
    entry.users += 1;
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      entry.users -= 1;
      setTimeout(() => {
        if (entry.users <= 0 && inflight.get(key) === entry) {
          entry.controller.abort();
          inflight.delete(key);
        }
      }, 400);
    };
    return { promise: entry.promise, release };
  }

  let lastConnect = { at: 0, signature: '' };
  let cleanupStarted = false;
  let worldOptions = null;

  // ── money ────────────────────────────────────────────────────────────────────────────────

  // Amounts in the page's currency use the theme's money format (what the cart shows); another
  // currency (a country of another market) is written the browser's way.
  function formatAmount(amount, currency, element) {
    const cents = Math.round(parseFloat(amount) * 100);
    if (Number.isNaN(cents)) return '';
    if (currency && currency !== element.dataset.currency) {
      try {
        return new Intl.NumberFormat(element.dataset.locale || undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
      } catch (error) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }
    return formatCents(cents, element.dataset.moneyFormat);
  }

  function formatCents(cents, moneyFormat) {
    const format = moneyFormat || '{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands, decimal) => {
      const value = (number / 100).toFixed(precision);
      const parts = value.split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (parts[1] ? decimal + parts[1] : '');
    };
    const match = format.match(placeholder);
    let value;
    switch (match && match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        return (cents / 100).toFixed(2);
    }
    return format.replace(placeholder, value);
  }

  // ── the calculator ───────────────────────────────────────────────────────────────────────

  class ShippingEstimator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.market = (this.dataset.market || '').toUpperCase();
      this.language = LANGUAGES.includes((this.dataset.language || '').toUpperCase().replace('-', '_'))
        ? this.dataset.language.toUpperCase().replace('-', '_')
        : null;
      this.strings = decodeAll(this.json('[data-se-strings]') || {});
      const cart = this.json('[data-se-cart]') || {};
      this.cart = { lines: cart.lines || [], discountCodes: cart.discountCodes || [] };
      this.signature = hash(JSON.stringify(this.cart));

      this.details = this.querySelector('.shipping-estimator__details');
      this.summaryText = this.querySelector('[data-se-summary-text]');
      this.summaryValue = this.querySelector('[data-se-summary]');
      this.countryEl = this.querySelector('[data-se-country]');
      this.zipField = this.querySelector('[data-se-zip-field]');
      this.zipEl = this.querySelector('[data-se-zip]');
      this.zipLabel = this.querySelector('[data-se-zip-label]');
      this.submitEl = this.querySelector('[data-se-submit]');
      this.resultsEl = this.querySelector('[data-se-results]');
      this.scroller = this.closest('.drawer__inner');
      if (!this.countryEl || !this.zipEl || !this.resultsEl || !this.summaryText) return;

      this.shippable = new Set(Array.from(this.countryEl.options).map((option) => option.value));
      this.buildCountries();
      this.bind();
      this.restore();
      this.cleanUpOldCartAddresses();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.typingTimer);
      if (this.request) this.request.release();
      this.request = null;
      if (this.scroller && this.onScroll) this.scroller.removeEventListener('scroll', this.onScroll);
      this.stopKeyboardFit();
    }

    json(selector) {
      const script = this.querySelector(selector);
      try {
        return script ? JSON.parse(script.textContent) : null;
      } catch (error) {
        return null;
      }
    }

    // Countries checkout ships to (rendered by Liquid in the shopper's language) sorted the way
    // the shopper reads them, then every other country under "Other countries".
    buildCountries() {
      const select = this.countryEl;
      const locale = this.dataset.locale || document.documentElement.lang || undefined;
      const options = Array.from(select.options);
      try {
        options
          .sort((a, b) => a.textContent.trim().localeCompare(b.textContent.trim(), locale))
          .forEach((option) => select.appendChild(option));
      } catch (error) {
        // keep Liquid's order
      }
      if (!worldOptions || worldOptions.locale !== locale) {
        let html = '';
        try {
          const names = new Intl.DisplayNames([locale || 'en'], { type: 'region' });
          html = WORLD.filter((code) => !this.shippable.has(code))
            .map((code) => [code, names.of(code)])
            .filter(([code, name]) => name && name !== code)
            .sort((a, b) => a[1].localeCompare(b[1], locale))
            .map(([code, name]) => `<option value="${code}">${escapeHtml(name)}</option>`)
            .join('');
        } catch (error) {
          html = '';
        }
        worldOptions = { locale, html };
      }
      if (worldOptions.html) {
        select.insertAdjacentHTML(
          'beforeend',
          `<optgroup label="${escapeHtml(this.strings.other_countries || '')}" data-se-other>${worldOptions.html}</optgroup>`
        );
      }
    }

    bind() {
      this.submitEl.addEventListener('click', () => this.price(this.fields(), { reason: 'button' }));
      this.zipEl.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault(); // the drawer sits next to the cart form: Enter must not start checkout
        this.price(this.fields(), { reason: 'button' });
      });
      this.zipEl.addEventListener('input', () => {
        this.saveDraft();
        clearTimeout(this.typingTimer);
        const fields = this.fields();
        const check = zipCheck(fields.country, fields.zip);
        // A complete postcode is priced on its own, no button needed.
        if (check === 'valid' || check === 'empty') {
          this.typingTimer = setTimeout(() => this.price(this.fields(), { reason: 'typing' }), TYPING_DELAY);
        }
      });
      this.zipEl.addEventListener('change', () => {
        clearTimeout(this.typingTimer);
        const fields = this.fields();
        if (!this.same(fields, this.active)) this.price(fields, { reason: 'button' });
      });
      this.countryEl.addEventListener('change', () => {
        const country = this.countryEl.value;
        const saved = readAddress();
        this.zipEl.value = (saved && saved.zips && saved.zips[country]) || '';
        this.updateZipField();
        this.saveDraft();
        this.price(this.fields(), { reason: 'country' });
      });
      this.details.addEventListener('toggle', () => {
        if (this.reopening) {
          this.reopening = false;
          return;
        }
        this.saveDraft();
      });
      this.addEventListener('focusin', (event) => {
        this.focused = event.target === this.zipEl ? 'zip' : event.target === this.countryEl ? 'country' : null;
        if (this.focused) {
          this.saveDraft();
          this.startKeyboardFit(event.target);
        }
      });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        // Chrome also fires focusout when the focused field is removed by a redraw: then the new
        // element (already connected by the next tick) must still see that the shopper was typing.
        setTimeout(() => {
          if (!this.isConnected) return;
          this.focused = null;
          this.saveDraft();
          this.stopKeyboardFit();
        }, 0);
      });
      this.resultsEl.addEventListener('click', (event) => {
        if (event.target.closest('[data-se-retry]')) this.price(this.active, { reason: 'retry' });
      });
      if (this.scroller) {
        this.onScroll = () => {
          if (this.scrollQueued) return;
          this.scrollQueued = true;
          requestAnimationFrame(() => {
            this.scrollQueued = false;
            this.saveDraft();
          });
        };
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    // ── state ──

    fields() {
      const country = this.countryEl.value;
      return { country, zip: NO_POSTCODE.includes(country) ? '' : cleanZip(this.zipEl.value) };
    }

    same(a, b) {
      return !!a && !!b && a.country === b.country && compactZip(a.zip) === compactZip(b.zip);
    }

    // Which address the summary prices and what the fields show:
    // 1. the shopper's own choice from the last 14 days, while the site's country is the same;
    // 2. after a switch of the site's country: that country, with its own remembered postcode;
    // 3. otherwise the site's country.
    // Fields typed in this visit and not priced yet (a redraw while typing) win over 1–3.
    restore() {
      const saved = readAddress();
      let active = { country: this.market, zip: '' };
      if (saved && saved.market === this.market && this.known(saved.country)) {
        active = { country: saved.country, zip: saved.zip || '' };
      } else if (saved && saved.zips && saved.zips[this.market]) {
        active = { country: this.market, zip: saved.zips[this.market] };
      }
      if (!this.known(active.country)) active = { country: this.countryEl.options[0].value, zip: '' };
      this.active = active;

      let shown = active;
      const draft = read(session, KEY_DRAFT);
      const age = draft ? Date.now() - (draft.at || 0) : Infinity;
      if (draft && draft.market === this.market && age < DRAFT_TTL && this.known(draft.country)) {
        shown = { country: draft.country, zip: draft.zip || '' };
      }
      this.countryEl.value = shown.country;
      this.zipEl.value = shown.zip;
      this.updateZipField();

      if (draft && draft.market === this.market && age < VIEW_TTL) {
        if (draft.open && !this.details.open) {
          this.reopening = true;
          this.details.open = true;
        }
        if (draft.scroll && this.scroller && this.closest('cart-drawer.active')) this.restoreScroll(draft.scroll);
        // Redrawn while the shopper was in a field (the AI chat redraws the drawer): keep them there.
        const visible = this.closest('cart-drawer.active') || !this.closest('cart-drawer');
        if (visible && draft.focus && Date.now() - (draft.focusAt || 0) < FOCUS_TTL && document.activeElement === document.body) {
          const field = draft.focus === 'zip' ? this.zipEl : this.countryEl;
          try {
            field.focus({ preventScroll: true });
            if (field === this.zipEl) field.setSelectionRange(field.value.length, field.value.length);
          } catch (error) {
            // ignore
          }
        }
      }

      const burst = lastConnect.signature && lastConnect.signature !== this.signature && Date.now() - lastConnect.at < BURST_WINDOW;
      lastConnect = { at: Date.now(), signature: this.signature };

      // Redrawn right after the shopper typed a complete postcode or picked another country (its
      // pricing was due on the old element): price what they typed, as if the pause had ended.
      if (!this.same(shown, active) && (!this.shippable.has(shown.country) || zipCheck(shown.country, shown.zip) !== 'invalid')) {
        this.price(shown, { reason: 'typing', delay: DEBOUNCE });
        return;
      }
      this.price(this.active, { reason: 'restore', delay: burst ? BURST_DEBOUNCE : DEBOUNCE });
    }

    known(country) {
      return !!country && Array.from(this.countryEl.options).some((option) => option.value === country);
    }

    saveDraft() {
      let scroll = this.scroller ? Math.round(this.scroller.scrollTop) : 0;
      if (this.restoringScroll) scroll = this.scrollTarget;
      const fields = { country: this.countryEl.value, zip: this.zipEl.value };
      write(session, KEY_DRAFT, {
        market: this.market,
        country: fields.country,
        zip: fields.zip,
        open: !!(this.details && this.details.open),
        scroll,
        focus: this.focused || null,
        focusAt: this.focused ? Date.now() : 0,
        at: Date.now(),
      });
    }

    key(address) {
      return [address.country, compactZip(address.zip), this.market, this.dataset.currency || '', this.language || '', this.signature].join('|');
    }

    // ── pricing ──

    price(address, { reason, delay = 0 } = {}) {
      clearTimeout(this.timer);
      clearTimeout(this.typingTimer);
      const explicit = reason === 'button' || reason === 'retry';
      const { country } = address;
      const zip = NO_POSTCODE.includes(country) ? '' : cleanZip(address.zip);
      const target = { country, zip };

      if (!this.cart.lines.length) return;

      if (!this.shippable.has(country)) {
        if (reason !== 'restore') saveAddress(this.market, country, '');
        this.active = target;
        this.release();
        this.render({ status: 'none' }, target);
        return;
      }

      const check = zipCheck(country, zip);
      if (check === 'invalid') {
        // Restoring an old postcode that no longer passes: fall back to the country price.
        if (reason === 'restore') return this.price({ country, zip: '' }, { reason, delay });
        if (reason === 'typing') return; // half-typed: wait
        this.active = target;
        this.release();
        this.render({ status: 'format' }, target);
        return;
      }

      this.active = target;
      const key = this.key(target);
      const cached = readQuote(key);
      if (cached) {
        this.release();
        this.render(cached.quote, target);
        if (reason !== 'restore' && reason !== 'retry') this.remember(target, cached.quote);
        if (!cached.fresh) this.fetch(key, target, { reason, quiet: true, delay: DEBOUNCE });
        return;
      }
      this.fetch(key, target, { reason, delay: explicit ? 0 : delay });
    }

    fetch(key, target, { reason, quiet = false, delay = 0 }) {
      this.release();
      if (!quiet) this.render({ status: 'loading' }, target);
      const run = () => {
        if (!this.isConnected) return;
        const request = sharedQuote(key, { country: target.country, zip: target.zip, cart: this.cart, language: this.language });
        this.request = request;
        request.promise
          .then((quote) => {
            if (this.request !== request) return;
            this.request = null;
            request.release();
            if (!this.isConnected || !this.same(target, this.active)) return;
            this.render(quote, target);
            if (reason !== 'restore') this.remember(target, quote);
          })
          .catch((error) => {
            if (this.request !== request) return;
            this.request = null;
            request.release();
            if (!this.isConnected || !this.same(target, this.active) || (error && error.name === 'AbortError')) return;
            if (!quiet) this.render({ status: 'error' }, target);
          });
      };
      if (delay > 0) this.timer = setTimeout(run, delay);
      else run();
    }

    release() {
      clearTimeout(this.timer);
      if (this.request) {
        this.request.release();
        this.request = null;
      }
    }

    // The shopper's choice is remembered once it gives an answer (a price, or "we don't deliver
    // there"); a postcode Shopify could not place is not.
    remember(target, quote) {
      if (quote.status === 'ok' || quote.status === 'none') saveAddress(this.market, target.country, quote.status === 'ok' ? target.zip : '');
    }

    // ── output ──

    render(quote, target) {
      this.state = quote.status;
      const strings = this.strings;
      const loading = quote.status === 'loading';
      this.classList.toggle('shipping-estimator--loading', loading);
      this.submitEl.disabled = loading;
      this.submitEl.setAttribute('aria-busy', loading ? 'true' : 'false');

      const place = [this.countryName(target.country), target.zip].filter(Boolean).join(', ');
      let summary = '';
      let html = '';
      let total = null;

      switch (quote.status) {
        case 'loading':
          summary = strings.calculating;
          html = `<p class="shipping-estimator__status shipping-estimator__status--loading">${escapeHtml(strings.calculating)}</p>`;
          break;
        case 'ok': {
          summary = this.priceLabel(quote);
          const crossCurrency = quote.currency && quote.currency !== this.dataset.currency;
          // The methods as checkout lists them, unless the price still depends on the postcode
          // (Spain, Italy, Portugal without one: "from X" and a hint).
          if (quote.options.length && !quote.zones) {
            html = `<p class="shipping-estimator__place">${escapeHtml(place)}</p>`;
            html += '<ul class="shipping-estimator__options" role="list">';
            html += quote.options
              .map(
                (option) =>
                  `<li class="shipping-estimator__option"><span class="shipping-estimator__option-name">${escapeHtml(
                    option.title
                  )}</span><span class="shipping-estimator__option-price${option.amount === 0 ? ' shipping-estimator__option-price--free' : ''}">${escapeHtml(
                    option.amount === 0 ? strings.free : formatAmount(option.amount, quote.currency, this)
                  )}</span></li>`
              )
              .join('');
            html += '</ul>';
            if (quote.options.length > 1) html += `<p class="shipping-estimator__note">${escapeHtml(strings.options_note)}</p>`;
          } else {
            html = `<p class="shipping-estimator__option shipping-estimator__option--single"><span class="shipping-estimator__option-name">${escapeHtml(
              place
            )}</span><span class="shipping-estimator__option-price${quote.price === 0 ? ' shipping-estimator__option-price--free' : ''}">${escapeHtml(
              summary
            )}</span></p>`;
            if (quote.zones) html += `<p class="shipping-estimator__note">${escapeHtml(strings.zip_hint)}</p>`;
          }
          if (crossCurrency) {
            html += `<p class="shipping-estimator__note">${escapeHtml(fill(strings.currency_note, { country: this.countryName(target.country), currency: quote.currency }))}</p>`;
          } else {
            total = quote.price;
          }
          break;
        }
        case 'format':
          summary = strings.check_zip_summary;
          html = `<p class="shipping-estimator__status shipping-estimator__status--error">${escapeHtml(
            fill(strings.invalid_zip, { example: zipExample(target.country) })
          )}</p>`;
          break;
        case 'invalid':
          summary = strings.check_zip_summary;
          html = `<p class="shipping-estimator__status shipping-estimator__status--error">${escapeHtml(
            fill(strings.zip_not_found, { zip: target.zip, country: this.countryName(target.country) })
          )}</p>`;
          break;
        case 'none':
          summary = strings.no_delivery_summary;
          html = `<p class="shipping-estimator__status shipping-estimator__status--error">${escapeHtml(
            fill(strings.no_delivery, { country: this.countryName(target.country) })
          )}</p>`;
          break;
        default:
          summary = strings.error_summary;
          html = `<p class="shipping-estimator__status shipping-estimator__status--error">${escapeHtml(strings.error)}</p>
            <button type="button" class="shipping-estimator__retry link" data-se-retry>${escapeHtml(strings.retry)}</button>`;
      }

      this.summaryText.textContent = summary || '';
      this.summaryValue.classList.toggle('shipping-estimator__summary-value--free', quote.status === 'ok' && quote.price === 0);
      this.summaryValue.classList.toggle('shipping-estimator__summary-value--muted', !['ok', 'loading'].includes(quote.status));
      this.resultsEl.innerHTML = html;
      this.applyTotal(total);
    }

    priceLabel(quote) {
      if (quote.price === 0) return quote.mainlandFree ? this.strings.free_mainland || this.strings.free : this.strings.free;
      const money = formatAmount(quote.price, quote.currency, this);
      return quote.several ? fill(this.strings.from_price || '[price]', { price: money }) : money;
    }

    countryName(code) {
      const option = Array.from(this.countryEl.options).find((item) => item.value === code);
      return option ? option.textContent.trim() : code;
    }

    updateZipField() {
      const country = this.countryEl.value;
      const none = NO_POSTCODE.includes(country);
      if (this.zipField) this.zipField.hidden = none;
      this.classList.toggle('shipping-estimator--no-zip', none);
      const example = zipExample(country);
      this.zipEl.placeholder = example || this.strings.zip || '';
      this.zipEl.inputMode = example && /^[\d\s-]+$/.test(example) && !['CZ', 'SK', 'SE', 'GR', 'PL', 'PT'].includes(country) ? 'numeric' : 'text';
      if (this.zipLabel) {
        this.zipLabel.textContent = OPTIONAL_POSTCODE.includes(country) ? this.strings.zip_optional || this.strings.zip : this.strings.zip;
      }
    }

    // "Estimated total" is rendered as goods only; with a delivery price in the same currency the
    // cheapest one (what checkout pre-selects) is added, so the shopper reads what they pay.
    applyTotal(rate) {
      const scope = this.closest('.cart-drawer, #main-cart-footer, .cart__footer') || document;
      const totalEl = scope.querySelector('.totals__total-value');
      if (!totalEl) return;
      if (totalEl.dataset.seOriginal === undefined) totalEl.dataset.seOriginal = totalEl.textContent;
      const cartCents = parseInt(this.dataset.cartTotal || '', 10);
      if (rate == null || Number.isNaN(cartCents)) {
        totalEl.textContent = totalEl.dataset.seOriginal;
        return;
      }
      totalEl.textContent = formatCents(cartCents + Math.round(rate * 100), this.dataset.moneyFormat);
    }

    // ── drawer behaviour ──

    // Same content, same height: put the shopper back where they were in the drawer. The content
    // keeps growing for a moment (images, the chat app's gift banner), so the position is
    // re-applied a few times, and left alone as soon as anything else moves it.
    restoreScroll(target) {
      this.restoringScroll = true;
      this.scrollTarget = target;
      let last = null;
      const apply = (delays) => {
        if (!this.isConnected || (last !== null && Math.abs(this.scroller.scrollTop - last) > 1)) {
          this.restoringScroll = false;
          return;
        }
        this.scroller.scrollTop = target;
        last = this.scroller.scrollTop;
        if (!delays.length || last >= target) {
          this.restoringScroll = false;
          return;
        }
        setTimeout(() => apply(delays.slice(1)), delays[0]);
      };
      apply([50, 150, 300, 500]);
    }

    // iPhone: the keyboard covers the bottom of the fixed drawer and Safari cannot scroll a fixed
    // element's content into view. While a field here has focus, the drawer is fitted to the
    // visible part of the screen and the field is scrolled into the middle of it.
    startKeyboardFit(field) {
      const viewport = window.visualViewport;
      const drawer = this.closest('cart-drawer');
      if (!viewport || !drawer || !window.matchMedia('(pointer: coarse)').matches) return;
      this.stopKeyboardFit();
      const fit = () => {
        if (!this.isConnected) return this.stopKeyboardFit();
        const covered = window.innerHeight - viewport.height > 80;
        drawer.style.top = covered ? `${Math.round(viewport.offsetTop)}px` : '';
        drawer.style.height = covered ? `${Math.round(viewport.height)}px` : '';
        if (covered && field.scrollIntoView) field.scrollIntoView({ block: 'center' });
      };
      this.keyboardFit = { viewport, drawer, fit, timer: setTimeout(fit, 350) };
      viewport.addEventListener('resize', fit);
      viewport.addEventListener('scroll', fit);
    }

    stopKeyboardFit() {
      const state = this.keyboardFit;
      if (!state) return;
      clearTimeout(state.timer);
      state.viewport.removeEventListener('resize', state.fit);
      state.viewport.removeEventListener('scroll', state.fit);
      state.drawer.style.top = '';
      state.drawer.style.height = '';
      this.keyboardFit = null;
    }

    // Until 01.10.2026 the calculator priced through /cart/shipping_rates.json, which saved the
    // priced address on the shopper's cart: checkout opened with a sample postcode (Madrid,
    // Tenerife, Rome…) or a bare typed postcode. Carts live for weeks, so once per visit such an
    // address is taken off the cart: a postcode with no street and no city that is one of the
    // old samples or the postcode typed in this calculator. An address typed at checkout stays.
    cleanUpOldCartAddresses() {
      if (cleanupStarted) return;
      cleanupStarted = true;
      if (!session || session.getItem(KEY_CLEANUP)) return;
      session.setItem(KEY_CLEANUP, '1');
      const OLD_SAMPLES = ['28013', '38002', '11000', '1001', '1100148', '9000018', '00185', '90133', 'D01F5P2', '010011', '10001', '10115', '75001', '00001', '11129', '1010', '1000', '1011AB', '1009', '11000', '1050', '1051', '00100', '10111', '81101', '10431', '10000', '01100', '0010', 'AD500', '98000', 'SW1A1AA', '8001', '101', '9490', 'VLT1010', '00120', '47890', '71000', '81000', '100'];
      fetch(`${this.dataset.cartUrl || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (!cart || !cart.token) return null;
          const id = `gid://shopify/Cart/${cart.token}`;
          const controller = new AbortController();
          return post(
            'query ShippingEstimatorCartAddresses($id: ID!) { cart(id: $id) { delivery { addresses { address { ... on CartDeliveryAddress { address1 city zip } } } } } }',
            { id },
            controller.signal
          ).then((body) => {
            const data = body.data.cart;
            const addresses = (data && data.delivery && data.delivery.addresses) || [];
            const saved = readAddress();
            const typed = new Set(Object.values((saved && saved.zips) || {}).map(compactZip));
            if (legacyTyped && legacyTyped.zip) typed.add(compactZip(legacyTyped.zip));
            const ours = (address) =>
              address && !address.address1 && !address.city && address.zip && (OLD_SAMPLES.includes(compactZip(address.zip)) || typed.has(compactZip(address.zip)));
            if (!addresses.length || !addresses.every((item) => ours(item.address))) return null;
            return post(
              'mutation ShippingEstimatorClearAddresses($id: ID!) { cartDeliveryAddressesReplace(cartId: $id, addresses: []) { userErrors { message } } }',
              { id },
              controller.signal
            );
          });
        })
        .catch(() => {});
    }
  }

  customElements.define('shipping-estimator', ShippingEstimator);

  // ── free-shipping bar ────────────────────────────────────────────────────────────────────

  // Liquid renders the bar exactly in the shop currency (EUR). In another currency Liquid has
  // converted the threshold like a product price (rate with the 2% fee, rounded up), while
  // checkout gives free delivery from the threshold at the rate without the fee. Re-done here so
  // the bar promises exactly what checkout gives (Poland: from 214.50 zł, not 219 zł).
  if (!customElements.get('free-shipping-bar')) {
    customElements.define(
      'free-shipping-bar',
      class FreeShippingBar extends HTMLElement {
        connectedCallback() {
          const currency = this.dataset.currency;
          const live = window.Shopify && window.Shopify.currency;
          if (!currency || currency === this.dataset.shopCurrency || !live || live.active !== currency) return;
          const rate = parseFloat(live.rate);
          const converted = parseInt(this.dataset.threshold, 10);
          const total = parseInt(this.dataset.cartTotal, 10);
          if (!(rate > 0) || Number.isNaN(converted) || Number.isNaN(total)) return;

          // Back to the shop currency: Liquid rounded the converted threshold, so snap to the
          // whole euro it came from (thresholds are whole euros: 49).
          let base = converted / rate;
          const whole = Math.round(base / 100) * 100;
          base = Math.abs(base - whole) <= 60 ? whole : Math.round(base);
          const threshold = Math.ceil((base * rate) / CHECKOUT_FX_FEE - 1e-6);

          let strings = {};
          try {
            strings = decodeAll(JSON.parse(this.querySelector('[data-fsb-strings]').textContent));
          } catch (error) {
            return;
          }
          const message = this.querySelector('[data-fsb-message]');
          const fillEl = this.querySelector('.cart-fsb__fill');
          const remaining = threshold - total;
          if (message) {
            if (remaining > 0) {
              const amount = `<strong>${escapeHtml(formatCents(remaining, this.dataset.moneyFormat))}</strong>`;
              message.innerHTML = escapeHtml(strings.welcome || '').split('[amount]').join(amount);
            } else {
              message.innerHTML = `<strong>${escapeHtml(strings.success || '')}</strong>`;
            }
          }
          if (fillEl) fillEl.style.width = `${Math.min(100, Math.floor((total * 100) / threshold))}%`;
          this.dataset.thresholdCheckout = String(threshold);
        }
      }
    );
  }

  void legacyCleaned;
})();
